import {
  ReactNode,
  useCallback,
  useEffect,
  createContext,
  useContext,
  useState,
} from "react";

import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { detectOSAndBrowser, isTestEnv } from "@smart/itops-utils-basic";

import { formsApi } from "./api";
import { useUser } from "./user";

type AnalyticsContextType = {
  isInit: boolean;
  platform?: "Desktop" | "Web";
  operatingSystem?: string;
  productVersion?: string;
  browserType?: string;
  sessionId?: string;
};

type AnalyticsEvent = {
  category: "identify" | "track";
  name?: string;
  screen?: string;
  entryPoint?: string;
  component?: string;
  jiraKey?: string;
  interactionType?: "Active" | "Passive";
  properties?: Record<string, any>;
};

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context && !isTestEnv()) {
    throw new Error(
      "useAnalyticsContext must be used within AnalyticsEventProvider",
    );
  }
  return isTestEnv()
    ? { isInit: false }
    : (context as NonNullable<typeof context>);
};

const queueEvent = async ({
  event,
  context,
  token,
  queueEventFn,
}: {
  event: AnalyticsEvent;
  context: AnalyticsContextType;
  token: string;
  queueEventFn: ReturnType<typeof formsApi.analyticsEventQueuer>["post"];
}) =>
  queueEventFn({
    body: {
      platform: context.platform,
      operatingSystem: context.operatingSystem,
      productVersion: context.productVersion,
      browserType: context.browserType,
      eventDateTime: new Date().getTime(),
      sessionId: context.sessionId,
      event,
    },
    token,
  });

export const useAnalyticsEvent = () => {
  const eventQueuer = formsApi.analyticsEventQueuer();
  const context = useAnalyticsContext();
  const { token } = useUser();

  const publishEvent = useCallback(
    async (
      event: Required<
        Pick<
          AnalyticsEvent,
          | "category"
          | "name"
          | "screen"
          | "entryPoint"
          | "component"
          | "jiraKey"
          | "interactionType"
        >
      > &
        AnalyticsEvent,
    ) => {
      if (!token) return;

      await queueEvent({
        event,
        context,
        token,
        queueEventFn: eventQueuer.post,
      });
    },
    [eventQueuer.post, context, token],
  );

  return { publishEvent };
};

export type AnalyticsEventProviderProps = { children: ReactNode };

export const AnalyticsEventProvider = ({
  children,
}: AnalyticsEventProviderProps) => {
  const { hostType, host, context } = useSmokeballApp();
  const { user, token } = useUser();
  const eventQueuer = formsApi.analyticsEventQueuer();

  const [contextValue, setContextValue] = useState<AnalyticsContextType>({
    isInit: false,
  });

  useEffect(() => {
    if (!contextValue.isInit && context && user?.userUri && token) {
      const { os, browser } = detectOSAndBrowser(navigator.userAgent);
      const contextValueToSet: AnalyticsContextType = {
        isInit: true,
        platform: hostType === "windows" ? "Desktop" : "Web",
        operatingSystem: os,
        productVersion: host?.version
          ? `${host.version.major}.${host.version.minor}.${host.version.revision}`
          : "",
        browserType: browser,
        sessionId: new Date().getTime().toString(),
      };
      queueEvent({
        event: {
          category: "identify",
        },
        context: contextValueToSet,
        token,
        queueEventFn: eventQueuer.post,
      }).catch(console.error);
      setContextValue(contextValueToSet);
    }
  }, [!!context, user?.userUri, token, contextValue.isInit]);

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};
